import { Button, Form, message, Modal, Select } from 'antd';
import React, { FC, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { associateAssetWithWorkOrder } from '../../thunks/work_orders_thunks';
import { assetsRestCrudThunksForSupplier } from '../../thunks/assets_thunks';
import { checkIsWarrantyActive, nullSafeGet } from '../../utils/DataAccessUtils';
import { getBuyerCompanySettingValue } from '../../utils/AuthUtils';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import AssetNumberDisplay from '../asset_row_display/AssetNumberDisplay';

const FORM_ID = 'work-order-tasks-form';

const SupplierAssociateAssetInWorkOrder: FC<any> = ({
	onCancel,
	onSuccess,
	workOrder,

	isClientView,

	fetchAssets,
	fetchMultipleAssets,
	associateAsset,

	userType,
	companyConfig,
	currentUser,
	assets,
}): React.ReactElement => {
	const [form] = Form.useForm();

	const [saving, setSaving] = useState(false);

	const updateAsset = useCallback(
		(values) => {
			return new Promise((resolve, reject) => {
				associateAsset(values, workOrder.id)
					.then(() => {
						resolve(workOrder);
					})
					.finally(() => {
						setSaving(false);
					});
			});
		},
		[workOrder, isClientView, associateAsset]
	);

	const handleSubmit = useCallback(
		(values) => {
			setSaving(true);
			updateAsset(values)
				.then((updatedWO) => {
					onSuccess && onSuccess();
				})
				.catch((err) => message.error(err))
				.finally(() => setSaving(false));
		},
		[onSuccess, updateAsset]
	);

	const isAreaFilterAllowed = getBuyerCompanySettingValue(userType)(
		'assetConfig.allowAreaAssociationWithAssets',
		companyConfig,
		currentUser,
		false
	);

	return (
		<Modal
			visible={true}
			width={800}
			title={`Work order details`}
			onCancel={onCancel}
			footer={[
				<Button onClick={onCancel} size="large">
					Cancel
				</Button>,
				<Button
					type="primary"
					size="large"
					style={{ marginLeft: '16px' }}
					key="submit"
					htmlType="submit"
					form={FORM_ID}
					loading={saving}
				>
					Save
				</Button>,
			]}
			closable={true}
		>
			<Form
				layout="vertical"
				onFinish={handleSubmit}
				id={FORM_ID}
				form={form}
				initialValues={{ assetId: workOrder.assetId }}
			>
				<Form.Item name="assetId" label="Affected Asset">
					<OWAsyncSelect
						style={{ maxWidth: 800 }}
						stateSlice={assets}
						allowClear={true}
						targetCollectionName="WORK_ORDER_FORM_ASSETS_AUTOCOMPLETE"
						fetchMultiple={(ids, targetCollectionName) => {
							fetchMultipleAssets(ids, targetCollectionName);
						}}
						fetchData={(
							searchText,
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						) =>
							fetchAssets(
								{ search: searchText },
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							)
						}
						renderRecord={(asset) => (
							<Select.Option key={asset.id} value={asset.id}>
								<div className="flex flex-row items-center">
									<div>{asset.name}</div>
									<AssetNumberDisplay asset={asset} style={{ marginLeft: 12 }} />
									{checkIsWarrantyActive(nullSafeGet('warranties.0', asset)) ? (
										<span className="ml-2 inline-block text-gray-400">(Warranty)</span>
									) : null}
								</div>
							</Select.Option>
						)}
						additionalFilters={{
							locationId: workOrder.locationId,
							problemTypeId: workOrder.problemTypeId,
							...(isAreaFilterAllowed &&
								workOrder.areaId && {
									areaIdOrNull: workOrder.areaId,
								}),
						}}
						dropdownRender={(menu) => <>{menu}</>}
						sortBy={{ sort_by: 'name', order: 'ascend' }}
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
	assets: state.assets,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchAsset: (id) => dispatch(assetsRestCrudThunksForSupplier.readOne(id)),
	fetchMultipleAssets: (ids, targetCollectionName) =>
		dispatch(assetsRestCrudThunksForSupplier.readLite({ ids }, targetCollectionName)),
	fetchAssets: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			assetsRestCrudThunksForSupplier.readLite(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	associateAsset: (entity, id) =>
		dispatch(associateAssetWithWorkOrder(ownProps.userType)(entity, id)),
});

const ComponentWithoutUserType = connect(
	mapStateToProps,
	mapDispatchToProps
)(SupplierAssociateAssetInWorkOrder);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
